import { useState, useEffect, useRef } from 'react';

const ArticlesPage = ({ onClose, animationStage, onNavigate }) => {
  const [contentStage, setContentStage] = useState('entering');
  const [visibleArticles, setVisibleArticles] = useState([]);
  const pageRef = useRef(null);

  // Articles data with tech-related images from Unsplash
  const articles = [
    {
      id: 1,
      title: "Privacy First",
      excerpt: "Browsers you should be using and those you should avoid in 2022 onwards",
      category: "Cybersecurity",
      readTime: "7 min read",
      date: "2022-11-15",
      image: "https://miro.medium.com/v2/resize:fit:1100/format:webp/0*G1fqEaa9lcZl8OTE.jpeg",
      metaImage: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop"
    },
    {
      id: 2,
      title: "Blockchain Beyond Cryptocurrency",
      excerpt: "Understanding blockchain's potential in supply chain management, digital identity, and secure data sharing across industries.",
      category: "Blockchain",
      readTime: "6 min read",
      date: "2024-01-12",
      image: "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=500&h=300&fit=crop",
      metaImage: "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=400&h=250&fit:crop"
    },
    {
      id: 3,
      title: "Quantum Computing: The Next Frontier",
      excerpt: "Demystifying quantum computing and its implications for cryptography, drug discovery, and complex system simulation.",
      category: "Quantum Computing",
      readTime: "10 min read",
      date: "2024-01-08",
      image: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=500&h=300&fit=crop",
      metaImage: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400&h=250&fit:crop"
    },
    {
      id: 4,
      title: "Sustainable Tech Solutions",
      excerpt: "How technology is driving sustainability through energy-efficient computing, green data centers, and eco-friendly innovation.",
      category: "Sustainability",
      readTime: "7 min read",
      date: "2024-01-05",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=500&h=300&fit:crop",
      metaImage: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&h=250&fit:crop"
    }
  ];

  useEffect(() => {
    if (animationStage === 'entering') {
      setContentStage('entering');
      const timer = setTimeout(() => setContentStage('visible'), 100);
      return () => clearTimeout(timer);
    } else if (animationStage === 'exiting') {
      setContentStage('exiting');
    }
  }, [animationStage]);

  // Intersection Observer for article animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const articleId = entry.target.getAttribute('data-article-id');
            setVisibleArticles(prev => [...new Set([...prev, articleId])]);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    // Added a small delay to ensure all elements are mounted before observing
    const timeout = setTimeout(() => {
      const articleElements = document.querySelectorAll('[data-article-id]');
      articleElements.forEach(el => observer.observe(el));
    }, 100);

    return () => {
      clearTimeout(timeout);
      observer.disconnect();
    };
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleFeaturedArticleClick = () => {
    window.open('https://medium.com/@fadhilinjagi/privacy-first-browsers-you-should-be-using-and-those-you-should-avoid-in-2021-c9c042342d25', '_blank');
  }


  return (
    <div 
      ref={pageRef}
      className="fixed inset-0 z-50 bg-slate-950 overflow-y-auto"
    >
      {/* Quantum Background */}
      <QuantumBackground />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-slate-800/50 backdrop-blur-xl bg-slate-950/70">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></div>
              <div className="flex items-center gap-3 text-white font-mono text-lg tracking-tight">
                <img 
                  src="/icons/favicon-32x32.png" 
                  alt="Nixque Icon" 
                  className="w-6 h-6"
                  onLoad={(e) => {
                    console.log('✅ IMAGE LOADED SUCCESSFULLY!');
                    console.log('Full URL:', e.target.src);
                    console.log('Dimensions:', e.target.naturalWidth, 'x', e.target.naturalHeight);
                  }}
                  onError={(e) => {
                    console.log('Attempted URL:', e.target.src);
                    console.log('Trying fallback paths...');
                    
                    // Try different paths
                    if (!e.target.dataset.retry1) {
                      e.target.dataset.retry1 = 'true';
                      console.log('Trying /favicon.ico');
                      e.target.src = "/favicon.ico";
                    } else if (!e.target.dataset.retry2) {
                      e.target.dataset.retry2 = 'true';
                      console.log('Trying /icons/favicon.ico');
                      e.target.src = "/icons/favicon.ico";
                    } else if (!e.target.dataset.retry3) {
                      e.target.dataset.retry3 = 'true';
                      console.log('Trying /icons/icon-32x32.png');
                      e.target.src = "/icons/icon-32x32.png";
                    } else {
                      console.log('All paths failed, showing placeholder');
                      e.target.style.display = 'none';
                      const placeholder = document.createElement('div');
                      placeholder.className = 'w-6 h-6 bg-cyan-400 rounded flex items-center justify-center border-2 border-green-500';
                      placeholder.innerHTML = '<span class="text-[8px] font-bold text-gray-900">IMG</span>';
                      e.target.parentNode.appendChild(placeholder);
                    }
                  }}
                />
                <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  NIXQUE LAB
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="group px-6 py-3 rounded-xl border border-slate-700 text-white hover:bg-slate-800/50 transition-all duration-300 font-mono text-sm tracking-wide hover:border-cyan-400/50 hover:text-cyan-300 backdrop-blur-sm flex items-center gap-2"
            >
              <span>RETURN TO MAIN</span>
              <div className="group-hover:translate-x-1 transition-transform duration-200">
                ↗
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="min-h-screen relative flex items-center justify-center overflow-hidden pt-20">
          <div className="max-w-7xl mx-auto px-6 py-16 w-full">
            <div className={`transition-all duration-1000 ${
              contentStage === 'entering' 
                ? 'opacity-0 transform translate-y-12 blur-sm' 
                : 'opacity-100 transform translate-y-0 blur-0'
            }`}>
              
              {/* Main Title */}
              <div className="text-center mb-16">
                <h1 className="text-4xl lg:text-6xl font-bold mb-6 font-mono">
                  <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                    INSIGHTS
                  </span>
                  <span className="text-white mx-2">&</span>
                  <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                    ARTICLES
                  </span>
                </h1>
                
                {/* Animated Loading Dash (Used pulse-slow-custom for unique look) */}
                <div className="relative inline-block mb-6">
                  <div className="w-12 h-0.5 bg-gradient-to-r from-cyan-400 to-purple-400 rounded-full animate-pulse-slow-custom"></div>
                  <div className="absolute inset-0 w-12 h-0.5 bg-gradient-to-r from-cyan-400 to-purple-400 rounded-full animate-loading-dash"></div>
                </div>
                
                <p className="text-lg lg:text-xl text-cyan-200/80 max-w-2xl mx-auto leading-relaxed font-light">
                  Exploring the frontiers of technology, innovation, and the future of digital transformation.
                </p>
              </div>

              {/* Featured Article - Enhanced Design */}
              <div className="max-w-4xl mx-auto mb-20">
                <div className="group cursor-pointer" onClick={handleFeaturedArticleClick}>
                  {/* Added border and hover transition to image container */}
                  <div className="relative overflow-hidden rounded-2xl mb-6 border border-slate-700 hover:border-cyan-400/50 transition-colors duration-500">
                    <div className="aspect-video bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-2xl overflow-hidden">
                      <img
                        src={articles[0].image}
                        alt={articles[0].title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 to-transparent"></div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 text-sm">
                      {/* Unique style improvement: added border to category tag */}
                      <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-full font-mono border border-cyan-500/50">
                        {articles[0].category}
                      </span>
                      <span className="text-slate-400 font-mono">{articles[0].readTime}</span>
                      <span className="text-slate-400 font-mono">{formatDate(articles[0].date)}</span>
                    </div>
                    
                    <h2 className="text-2xl lg:text-3xl font-bold text-white font-mono group-hover:text-cyan-400 transition-colors duration-300">
                      {articles[0].title}
                    </h2>
                    
                    <p className="text-slate-300 text-lg leading-relaxed">
                      {articles[0].excerpt}
                    </p>
                    
                    <button 
                      onClick={handleFeaturedArticleClick}
                      className="group inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-cyan-400/30 text-cyan-400 hover:bg-cyan-400/10 transition-all duration-300 font-mono text-sm"
                    >
                      <span>READ FULL ARTICLE</span>
                      <div className="group-hover:translate-x-1 transition-transform duration-200">
                        →
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Articles Grid Section */}
        <section className="py-20 lg:py-32 bg-slate-900/20 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl lg:text-4xl font-bold text-white text-center mb-4 font-mono">
              LATEST INSIGHTS
            </h2>
            <p className="text-slate-400 text-center mb-12 lg:mb-16 max-w-2xl mx-auto">
              Dive deeper into the technologies shaping our future
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.slice(1).map((article, index) => (
                <article
                  key={article.id}
                  data-article-id={article.id}
                  // HIGH THINKING / TOP UNIQUENESS: 3D Hover Effect
                  className={`group bg-slate-900/30 backdrop-blur-sm rounded-2xl border border-slate-800/50 overflow-hidden 
                            transition-all duration-700 hover:border-cyan-400/30 
                            hover:transform hover:scale-[1.02] hover:shadow-cyan-500/20 hover:shadow-2xl hover:rotate-1 
                            ${
                              visibleArticles.includes(article.id.toString()) 
                                ? 'opacity-100 transform translate-y-0 rotate-0' 
                                : 'opacity-0 transform translate-y-8 rotate-1' // Subtle initial rotation on entry
                            }`}
                  style={{
                    transitionDelay: visibleArticles.includes(article.id.toString()) ? `${index * 200}ms` : '0ms'
                  }}
                >
                  {/* Article Image - Subtle tilt/glow on hover */}
                  <div className="relative overflow-hidden aspect-video">
                    <img
                      src={article.metaImage}
                      alt={article.title}
                      // Added group-hover:rotate-1
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 group-hover:rotate-1" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent"></div>
                    <div className="absolute top-4 left-4">
                      <span className="px-2 py-1 bg-slate-950/80 text-cyan-400 text-xs rounded-full font-mono backdrop-blur-sm border border-cyan-500/50">
                        {article.category}
                      </span>
                    </div>
                  </div>

                  {/* Article Content */}
                  <div className="p-6">
                    <div className="flex items-center justify-between text-sm text-slate-400 mb-3">
                      <span className="font-mono">{article.readTime}</span>
                      <span className="font-mono">{formatDate(article.date)}</span>
                    </div>
                    
                    <h3 className="text-xl font-bold text-white mb-3 font-mono group-hover:text-cyan-400 transition-colors duration-300 line-clamp-2">
                      {article.title}
                    </h3>
                    
                    <p className="text-slate-300 text-sm leading-relaxed mb-4 line-clamp-3">
                      {article.excerpt}
                    </p>
                    
                    <button 
                      onClick={() => window.open(`https://example.com/article-${article.id}`, '_blank')}
                      className="group w-full flex items-center justify-between px-3 py-2 rounded-lg border border-slate-700 text-slate-400 hover:border-cyan-400/50 hover:text-cyan-400 hover:bg-cyan-400/10 transition-all duration-300 font-mono text-xs"
                    >
                      <span>READ MORE</span>
                      <div className="group-hover:translate-x-1 transition-transform duration-200">
                        →
                      </div>
                    </button>
                  </div>
                </article>
              ))}
            </div>

            {/* Load More Button - Unique style improvement: added glow/shadow */}
            <div className="text-center mt-12">
              <button 
                onClick={() => window.open('https://example.com/all-articles', '_blank')}
                className="px-6 py-3 rounded-xl border border-slate-700 text-slate-400 hover:border-cyan-400/50 hover:text-cyan-400 transition-all duration-300 font-mono text-sm backdrop-blur-sm 
                           hover:shadow-lg hover:shadow-cyan-500/20"
              >
                LOAD MORE ARTICLES
              </button>
            </div>
          </div>
        </section>

        {/* Newsletter Section - Unique style improvement: added more background distinction */}
        <section className="py-20 lg:py-32 border-y border-slate-800/50 bg-slate-900/40">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className={`transition-all duration-1000 ${
              contentStage === 'entering' 
                ? 'opacity-0 transform translate-y-8' 
                : 'opacity-100 transform translate-y-0'
            }`}>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6 lg:mb-8 font-mono">
                STAY UPDATED
              </h2>
              <p className="text-base lg:text-lg text-slate-300 leading-relaxed max-w-2xl mx-auto mb-8">
                Get the latest insights on technology and innovation delivered directly to your inbox.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-700 bg-slate-900/50 text-white placeholder-slate-400 focus:outline-none focus:border-cyan-400/50 transition-all duration-300 font-mono text-sm"
                />
                <button className="px-4 py-2 rounded-lg bg-cyan-500 text-white hover:bg-cyan-600 transition-all duration-300 font-mono text-sm whitespace-nowrap">
                  SUBSCRIBE
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Your Footer Component */}
      <Footer onNavigate={onNavigate} />

      <style jsx>{`
        /* --- General Animations --- */
        @keyframes loading-dash {
          0% {
            transform: scaleX(0);
            transform-origin: left; /* Added origin for sweep effect */
            opacity: 1;
          }
          50% {
            transform: scaleX(1);
            transform-origin: left;
            opacity: 1;
          }
          100% {
            transform: scaleX(0);
            transform-origin: right; /* Added origin for sweep effect */
            opacity: 0;
          }
        }
        .animate-loading-dash {
          animation: loading-dash 2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
        }
        @keyframes pulse-slow-custom { /* Unique name to differentiate from built-in pulse */
            0%, 100% { opacity: 0.3; }
            50% { opacity: 0.1; }
        }
        .animate-pulse-slow-custom {
          animation: pulse-slow-custom 4s ease-in-out infinite;
        }

        /* --- Quantum Background Animations --- */
        @keyframes float-quantum {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg) scale(1);
            opacity: 0.3;
          }
          33% { 
            transform: translateY(-15px) rotate(120deg) scale(1.1);
            opacity: 0.6;
          }
          66% { 
            transform: translateY(10px) rotate(240deg) scale(0.9);
            opacity: 0.4;
          }
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.1; }
          50% { opacity: 0.3; }
        }
        .animate-float-quantum {
          animation: float-quantum 8s ease-in-out infinite;
        }
        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }

        /* --- Geometric Clip Paths for Unique Background Shapes --- */
        .clip-hexagon {
          clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
        }
        .clip-pentagon {
          clip-path: polygon(50% 0%, 100% 38%, 81% 100%, 19% 100%, 0% 38%);
        }

        /* --- Utility Classes for Article Cards --- */
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

// Quantum Background Component (Kept local as requested)
const QuantumBackground = () => {
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Quantum Grid */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(6,182,212,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(6,182,212,0.1)_1px,transparent_1px)] bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)]"></div>
      </div>

      {/* Floating Quantum Particles */}
      <div className="absolute inset-0">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-cyan-500/20 animate-float-quantum"
            style={{
              width: `${20 + i * 15}px`,
              height: `${20 + i * 15}px`,
              left: `${(i * 12) % 100}%`,
              top: `${(i * 15) % 100}%`,
              animationDelay: `${i * 2}s`,
              filter: 'blur(12px)',
            }}
          />
        ))}
      </div>

      {/* Geometric Shapes - Uses the new clip-path classes defined in the style block */}
      <div className="absolute inset-0">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className={`absolute border border-cyan-400/30 animate-pulse-slow ${
              i % 2 === 0 ? 'clip-hexagon' : 'clip-pentagon'
            }`}
            style={{
              width: '80px',
              height: '80px',
              left: `${(i * 20) % 100}%`,
              top: `${(i * 25) % 100}%`,
              animationDelay: `${i * 1.5}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
};

// Your Footer Component (Kept local as requested)
const Footer = ({ onNavigate }) => {
  const [currentYear] = useState(new Date().getFullYear());

  const socialLinks = [
    {
      name: 'X',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
      url: 'https://x.com/nixque'
    },
    {
      name: 'Instagram',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      ),
      url: 'https://instagram.com/nixque'
    },
    {
      name: 'Facebook',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
      url: 'https://facebook.com/nixque'
    },
    {
      name: 'Reddit',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
        </svg>
      ),
      url: 'https://reddit.com/r/nixque'
    }
  ];

  const quickLinks = [
    { name: 'What We Do', page: 'what-we-do' },
    { name: 'About Us', page: 'about' },
    { name: 'Articles', page: 'articles' },
    { name: 'Contact', page: 'contact' }
  ];

  const handleNavigation = (page) => {
    if (onNavigate) {
      onNavigate(page);
    }
  };

  return (
    <footer className="relative border-t border-slate-800 bg-slate-950/50 backdrop-blur-xl">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/20 pointer-events-none"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          
          {/* Brand Column */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></div>
              <div className="text-white font-mono text-lg font-bold tracking-tight bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                NIXQUE
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed max-w-md mb-6">
              Building the future through innovative technology solutions. 
              We create digital experiences that transform businesses and empower growth.
            </p>
            
            {/* Social Links */}
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg border border-slate-800 bg-slate-900/30 text-slate-400 hover:text-cyan-400 hover:border-cyan-400/30 hover:bg-cyan-400/10 transition-all duration-300"
                  aria-label={`Follow us on ${social.name}`}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-mono text-sm font-semibold mb-4 uppercase tracking-wider">
              Navigation
            </h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => handleNavigation(link.page)}
                    className="text-slate-400 hover:text-cyan-400 text-sm transition-colors duration-300 font-mono hover:underline cursor-pointer"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-mono text-sm font-semibold mb-4 uppercase tracking-wider">
              Connect
            </h3>
            <div className="space-y-2 text-sm">
              <a
                href="mailto:contact@nixque.com"
                className="block text-slate-400 hover:text-cyan-400 transition-colors duration-300 font-mono"
              >
                contact@nixque.com
              </a>
              <a
                href="tel:+15551234567"
                className="block text-slate-400 hover:text-cyan-400 transition-colors duration-300 font-mono"
              >
                +2547 42 606971
              </a>
              <p className="text-slate-400 font-mono">
                Nairobi | Kisumu, Kenya
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Copyright */}
            <div className="text-slate-500 text-sm font-mono">
              © {currentYear} NIXQUE LTD. All rights reserved.
            </div>

            {/* Legal Links */}
            <div className="flex gap-6 text-sm">
              <button
                onClick={() => handleNavigation('privacy')}
                className="text-slate-500 hover:text-slate-300 transition-colors duration-300 font-mono"
              >
                Privacy
              </button>
              <button
                onClick={() => handleNavigation('terms')}
                className="text-slate-500 hover:text-slate-300 transition-colors duration-300 font-mono"
              >
                Terms
              </button>
              <button
                onClick={() => handleNavigation('cookies')}
                className="text-slate-500 hover:text-slate-300 transition-colors duration-300 font-mono"
              >
                Cookies
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default ArticlesPage;